import { IconButton } from "$components/button/icon-button";
import { BinIcon } from "$components/icons";
import type { OptionType } from "$components/inputs";
import {
  SelectField as RHFSelectField,
  TextField as RHFTextField,
} from "$components/rhf-fields";
import { styled, type Theme } from "@material-ui/core";
import type { ComponentProps } from "react";
import {
  InvoiceCardSection,
  useInvoiceCardLayout,
} from "../invoice-card-shell";
import type {
  InvoiceLineItemDraftRow,
  InvoiceLineItemRow,
} from "./invoice-line-item-row";
import type { InvoiceItemOption } from "./invoice-view";
import { PriceListItemField } from "./price-list-item-field";
import {
  INVOICE_CARD_BORDER_WIDTH,
  INVOICE_CARD_COLORS,
  type InvoicePayerKind,
  invoiceAccentColor,
  invoiceAmountColor,
  invoiceRowBackgroundColor,
} from "./tokens";

type InvoiceLineItemsVariant = "draft" | "finalised";

// Supplier is wider than the design's 104/96px: those were drawn against the
// prototype's sample suppliers, and the real `LineItemSupplier` vocabulary
// ({Government, Private}) puts "Government" under the draft's dropdown arrow.
// The extra width comes out of Item's `1fr`, so the draft's intrinsic minimum
// (180px Item floor + fixed columns) still lands on DRAFT_MIN_WIDTH rather than
// pushing past it.
const DRAFT_COLUMNS = "minmax(180px, 1fr) 122px 118px 48px 210px 122px";
const DRAFT_MIN_WIDTH = 800;
const FINALISED_COLUMNS =
  "minmax(200px, 1fr) 118px 110px 44px 96px 148px 116px";
const FINALISED_MIN_WIDTH = 880;

/**
 * Below its own column minimum a row can only be read by scrolling it sideways,
 * so the lines render as the stacked variant instead. Draft and finalised carry
 * different columns, so the switch legitimately lands in a different place for
 * each. The card is measured border-box, which the table's content-box minimum
 * has to clear.
 */
export const stacksLineItems = (
  variant: InvoiceLineItemsVariant,
  cardWidth: number,
): boolean =>
  cardWidth <
  (variant === "draft" ? DRAFT_MIN_WIDTH : FINALISED_MIN_WIDTH) +
    INVOICE_CARD_BORDER_WIDTH * 2;

const DATE_COLUMN_WIDTH = 46;
/** The dense inputs carry 8px of lead padding; the cell supplies the rest of padX. */
const DENSE_INPUT_PAD_X = 8;

/**
 * The draft table holds borderless-feeling fields: the input has to fill its
 * cell, which means overriding MUI's own outlined-input chrome. Reached through
 * the container's descendant selectors because MUI v4's `styled()` can emit a
 * rule-less class when it wraps a function component.
 */
const denseFieldStyles = (theme: Theme) =>
  ({
    "& .MuiFormControl-root, & .MuiInputBase-root": { width: "100%" },
    "& .MuiInputBase-root": { padding: 0, minHeight: 0 },
    "& .MuiOutlinedInput-root": {
      borderRadius: 0,
      backgroundColor: "transparent",
      "&:hover": { backgroundColor: INVOICE_CARD_COLORS.pageTrack },
      "&.Mui-focused": {
        backgroundColor: theme.palette.background.paper,
        boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
      },
    },
    "& .MuiOutlinedInput-notchedOutline": { border: 0 },
    "& .MuiOutlinedInput-input, & .MuiSelect-select": {
      padding: `9px ${DENSE_INPUT_PAD_X}px`,
      fontSize: 13.5,
      lineHeight: 1.3,
      minHeight: 0,
      height: "auto",
    },
    // Selects wrap rather than ellipsize, so a column width can never truncate a value.
    "& .MuiSelect-select": {
      paddingRight: 20,
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "clip",
    },
    "& .MuiSelect-icon": {
      right: 0,
      width: 18,
      color: theme.palette.text.secondary,
    },
    // The autocomplete brings its own outlined-input padding at a specificity
    // the rules above can't reach, so the item cell is re-flattened through the
    // component's own class chain.
    "& .MuiAutocomplete-root .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root']":
      {
        padding: 0,
      },
    "& .MuiAutocomplete-root .MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root'] .MuiAutocomplete-input":
      {
        padding: `9px ${DENSE_INPUT_PAD_X}px`,
        paddingRight: 20,
      },
    // MUI positions the adornment at `calc(50% - 14px)`, an offset sized for a
    // full-height field; against the flattened dense row it lands above centre
    // and the item column's arrow no longer lines up with the other columns'.
    "& .MuiAutocomplete-root .MuiAutocomplete-endAdornment": {
      right: 0,
      top: "50%",
      transform: "translateY(-50%)",
    },
    "& .MuiAutocomplete-popupIndicator": {
      padding: 0,
      marginRight: 0,
      color: theme.palette.text.secondary,
      "& .MuiSvgIcon-root": { fontSize: 18 },
    },
  }) as const;

const ScrollArea = styled("div")<Theme, { "data-dense": boolean }>(
  ({ theme, ...props }) => ({
    overflowX: "auto",
    ...(props["data-dense"] ? denseFieldStyles(theme) : {}),
  }),
);

const HeaderRow = styled("div")<
  Theme,
  { "data-columns": string; "data-min-width": number }
>(({ theme, ...props }) => ({
  display: "grid",
  gridTemplateColumns: props["data-columns"],
  minWidth: props["data-min-width"],
  alignItems: "center",
  backgroundColor: INVOICE_CARD_COLORS.altSurface,
  borderBottom: `1px solid ${INVOICE_CARD_COLORS.border}`,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: theme.palette.text.secondary,
}));

const HeaderCell = styled("div")<
  Theme,
  { "data-align"?: "left" | "center" | "right" }
>(({ ...props }) => ({
  padding: props["data-align"] === "center" ? "8px 2px" : 8,
  textAlign: props["data-align"] ?? "left",
}));

const HeaderItemCell = styled("div")<Theme, { "data-pad-left": number }>(
  ({ ...props }) => ({
    padding: `8px 8px 8px ${props["data-pad-left"]}px`,
    display: "flex",
    gap: 10,
    position: "sticky",
    left: 0,
    zIndex: 2,
    backgroundColor: INVOICE_CARD_COLORS.altSurface,
  }),
);

const HeaderAmountCell = styled("div")<Theme, { "data-pad-right": number }>(
  ({ ...props }) => ({
    padding: `8px ${props["data-pad-right"]}px 8px 8px`,
    textAlign: "right",
    position: "sticky",
    right: 0,
    zIndex: 2,
    backgroundColor: INVOICE_CARD_COLORS.altSurface,
  }),
);

const HeaderDateColumn = styled("span")({
  width: DATE_COLUMN_WIDTH,
  flex: `0 0 ${DATE_COLUMN_WIDTH}px`,
});

const Row = styled("div")<
  Theme,
  {
    "data-columns": string;
    "data-min-width": number;
    "data-payer-kind": InvoicePayerKind;
    "data-align": "center" | "stretch";
  }
>(({ theme, ...props }) => ({
  display: "grid",
  gridTemplateColumns: props["data-columns"],
  minWidth: props["data-min-width"],
  alignItems: props["data-align"],
  borderBottom: `1px solid ${INVOICE_CARD_COLORS.divider}`,
  backgroundColor: invoiceRowBackgroundColor(theme, props["data-payer-kind"]),
}));

const DateColumn = styled("div")<Theme, { "data-pad": string }>(
  ({ theme, ...props }) => ({
    width: DATE_COLUMN_WIDTH,
    flex: `0 0 ${DATE_COLUMN_WIDTH}px`,
    padding: props["data-pad"],
    fontWeight: 500,
    fontSize: 12,
    color: theme.palette.text.secondary,
  }),
);

const DraftCell = styled("div")({
  borderRight: `1px solid ${INVOICE_CARD_COLORS.divider}`,
  display: "flex",
  alignItems: "center",
  minWidth: 0,
});

const DraftQuantityCell = styled(DraftCell)({
  "& input": { textAlign: "center", paddingLeft: 2, paddingRight: 2 },
});

/**
 * Sticky cells repaint the row background themselves — without it the scrolled
 * columns show through as they pass beneath.
 */
const DraftItemCell = styled("div")<
  Theme,
  { "data-payer-kind": InvoicePayerKind; "data-pad-left": number }
>(({ theme, ...props }) => ({
  borderRight: `1px solid ${INVOICE_CARD_COLORS.divider}`,
  display: "flex",
  alignItems: "center",
  gap: 10,
  minWidth: 0,
  paddingLeft: props["data-pad-left"],
  position: "sticky",
  left: 0,
  zIndex: 2,
  backgroundColor: invoiceRowBackgroundColor(theme, props["data-payer-kind"]),
}));

const DraftAmountCell = styled("div")<
  Theme,
  { "data-payer-kind": InvoicePayerKind }
>(({ theme, ...props }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 2,
  padding: "4px 8px 4px 6px",
  borderLeft: `1px solid ${INVOICE_CARD_COLORS.divider}`,
  position: "sticky",
  right: 0,
  zIndex: 2,
  backgroundColor: invoiceRowBackgroundColor(theme, props["data-payer-kind"]),
  "& .MuiIconButton-root": { padding: 7 },
  "& .MuiSvgIcon-root": { fontSize: 16 },
}));

const DraftAmount = styled("div")<
  Theme,
  { "data-payer-kind": InvoicePayerKind }
>(({ theme, ...props }) => ({
  fontWeight: 600,
  fontSize: 14,
  whiteSpace: "nowrap",
  color: invoiceAmountColor(theme, props["data-payer-kind"]),
}));

const FinalisedRow = styled(Row)<Theme>(({ theme }) => ({
  fontWeight: 500,
  fontSize: 13.5,
  color: theme.palette.text.primary,
}));

const FinalisedItemCell = styled("div")<
  Theme,
  { "data-payer-kind": InvoicePayerKind; "data-pad-left": number }
>(({ theme, ...props }) => ({
  padding: `11px 8px 11px ${props["data-pad-left"]}px`,
  display: "flex",
  gap: 10,
  minWidth: 0,
  position: "sticky",
  left: 0,
  zIndex: 2,
  backgroundColor: invoiceRowBackgroundColor(theme, props["data-payer-kind"]),
}));

const FinalisedItemName = styled("div")({
  minWidth: 0,
  textWrap: "pretty",
});

const FinalisedCell = styled("div")<
  Theme,
  { "data-align"?: "left" | "center" | "right" }
>(({ ...props }) => ({
  padding: props["data-align"] === "center" ? "11px 2px" : "11px 8px",
  textAlign: props["data-align"] ?? "left",
  whiteSpace: props["data-align"] === "right" ? "nowrap" : undefined,
  color: INVOICE_CARD_COLORS.mutedBody,
}));

const FinalisedPayerCell = styled("div")<
  Theme,
  { "data-payer-kind": InvoicePayerKind }
>(({ theme, ...props }) => ({
  padding: "11px 8px",
  minWidth: 0,
  fontWeight: 600,
  fontSize: 13,
  color: invoiceAccentColor(theme, props["data-payer-kind"]),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));

const FinalisedAmountCell = styled("div")<
  Theme,
  { "data-payer-kind": InvoicePayerKind; "data-pad-right": number }
>(({ theme, ...props }) => ({
  padding: `11px ${props["data-pad-right"]}px 11px 8px`,
  textAlign: "right",
  fontWeight: 600,
  fontSize: 14,
  whiteSpace: "nowrap",
  position: "sticky",
  right: 0,
  zIndex: 2,
  color: invoiceAmountColor(theme, props["data-payer-kind"]),
  backgroundColor: invoiceRowBackgroundColor(theme, props["data-payer-kind"]),
}));

/**
 * A CSS grid rather than a table element so the Item and Amount columns can
 * stay stuck to the edges while the middle scrolls.
 */
export const InvoiceLineItems: FC<
  {
    showItemDates?: boolean;
  } & (
    | {
        variant: "draft";
        rows: InvoiceLineItemDraftRow[];
        itemOptions: InvoiceItemOption[];
        departmentOptions: OptionType[];
        supplierOptions: OptionType[];
        onRemoveItem: (rowId: string) => void;
        removeItemLabel?: string;
      }
    | {
        variant: "finalised";
        rows: InvoiceLineItemRow[];
        itemOptions?: never;
        departmentOptions?: never;
        supplierOptions?: never;
        onRemoveItem?: never;
        removeItemLabel?: never;
      }
  ) &
    Omit<ComponentProps<typeof InvoiceCardSection>, "children">
> = ({
  variant,
  rows,
  showItemDates = true,
  itemOptions,
  departmentOptions,
  supplierOptions,
  onRemoveItem,
  removeItemLabel = "Remove item",
  ...restProps
}) => {
  const { padX } = useInvoiceCardLayout();

  const isDraft = variant === "draft";
  const columns = isDraft ? DRAFT_COLUMNS : FINALISED_COLUMNS;
  const minWidth = isDraft ? DRAFT_MIN_WIDTH : FINALISED_MIN_WIDTH;

  return (
    <InvoiceCardSection
      paddingTop={0}
      paddingBottom={0}
      disablePadX
      {...restProps}
    >
      <ScrollArea data-dense={isDraft}>
        <HeaderRow data-columns={columns} data-min-width={minWidth}>
          <HeaderItemCell data-pad-left={padX}>
            {showItemDates && <HeaderDateColumn>Date</HeaderDateColumn>}
            <span>Item</span>
          </HeaderItemCell>
          <HeaderCell>Revenue dpt</HeaderCell>
          <HeaderCell>Supplier</HeaderCell>
          <HeaderCell data-align="center">Qty</HeaderCell>
          {!isDraft && <HeaderCell data-align="right">Unit price</HeaderCell>}
          <HeaderCell>Payer &amp; tariff</HeaderCell>
          <HeaderAmountCell data-pad-right={padX}>Amount</HeaderAmountCell>
        </HeaderRow>

        {isDraft
          ? rows.map((row) => (
              <Row
                key={row.id}
                data-columns={columns}
                data-min-width={minWidth}
                data-payer-kind={row.payerKind}
                data-align="stretch"
              >
                <DraftItemCell
                  data-payer-kind={row.payerKind}
                  data-pad-left={padX - DENSE_INPUT_PAD_X}
                >
                  {showItemDates && (
                    <DateColumn data-pad={`0 0 0 ${DENSE_INPUT_PAD_X}px`}>
                      {row.dateLabel}
                    </DateColumn>
                  )}
                  <PriceListItemField
                    name={row.fieldNames.name}
                    options={itemOptions}
                  />
                </DraftItemCell>
                <DraftCell>
                  <RHFSelectField
                    name={row.fieldNames.department}
                    options={departmentOptions}
                    fullWidth
                    hideHelper
                  />
                </DraftCell>
                <DraftCell>
                  <RHFSelectField
                    name={row.fieldNames.supplier}
                    options={supplierOptions}
                    fullWidth
                    hideHelper
                  />
                </DraftCell>
                <DraftQuantityCell>
                  <RHFTextField
                    name={row.fieldNames.quantity}
                    fullWidth
                    hideHelper
                  />
                </DraftQuantityCell>
                <DraftCell>
                  <RHFSelectField
                    name={row.fieldNames.payer}
                    options={row.payerOptions}
                    fullWidth
                    hideHelper
                  />
                </DraftCell>
                <DraftAmountCell data-payer-kind={row.payerKind}>
                  <DraftAmount data-payer-kind={row.payerKind}>
                    {row.amountLabel}
                  </DraftAmount>
                  <IconButton
                    color="error"
                    size="small"
                    aria-label={removeItemLabel}
                    onClick={() => onRemoveItem(row.id)}
                  >
                    <BinIcon fill="currentColor" />
                  </IconButton>
                </DraftAmountCell>
              </Row>
            ))
          : rows.map((row) => (
              <FinalisedRow
                key={row.id}
                data-columns={columns}
                data-min-width={minWidth}
                data-payer-kind={row.payerKind}
                data-align="center"
              >
                <FinalisedItemCell
                  data-payer-kind={row.payerKind}
                  data-pad-left={padX}
                >
                  {showItemDates && (
                    <DateColumn data-pad="1px 0 0">{row.dateLabel}</DateColumn>
                  )}
                  <FinalisedItemName>{row.name}</FinalisedItemName>
                </FinalisedItemCell>
                <FinalisedCell>{row.departmentLabel}</FinalisedCell>
                <FinalisedCell>{row.supplierLabel}</FinalisedCell>
                <FinalisedCell data-align="center">
                  {row.quantityLabel}
                </FinalisedCell>
                <FinalisedCell data-align="right">
                  {row.unitPriceLabel}
                </FinalisedCell>
                <FinalisedPayerCell data-payer-kind={row.payerKind}>
                  {row.payerLabel}
                </FinalisedPayerCell>
                <FinalisedAmountCell
                  data-payer-kind={row.payerKind}
                  data-pad-right={padX}
                >
                  {row.amountLabel}
                </FinalisedAmountCell>
              </FinalisedRow>
            ))}
      </ScrollArea>
    </InvoiceCardSection>
  );
};
